import React from 'react'
import DocumentDialog, { DocumentSection } from '../DocumentDialog'

interface PaymentConfirmationDialogProps {
  data: any
  onSave?: (updatedData: any, changes?: any[]) => void
  shipmentId: string
  documentId: string
}

const paymentConfirmationSections: DocumentSection[] = [
  {
    id: 'documentInfo',
    title: 'Document Information',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Confirmation Number', path: 'confirmationNumber' },
      { type: 'field', label: 'Reference Number', path: 'referenceNumber' },
      { type: 'field', label: 'Receipt Number', path: 'receiptNumber' },
      { type: 'field', label: 'Issued Date', path: 'issuedDate' },
      { type: 'field', label: 'Issued By', path: 'issuedBy' },
      { type: 'field', label: 'Status', path: 'status' },
    ],
  },
  {
    id: 'paymentDetails',
    title: 'Payment Details',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Payment Date', path: 'paymentDate' },
      { type: 'field', label: 'Payment Amount', path: 'paymentAmount' },
      { type: 'field', label: 'Currency', path: 'currency' },
      { type: 'field', label: 'Payment Method', path: 'paymentMethod' },
      { type: 'field', label: 'Payment Status', path: 'paymentStatus' },
      {
        type: 'array',
        label: 'Invoice Numbers',
        path: 'invoiceNumbers',
        columns: [{ label: 'Invoice Number', path: '.' }],
      },
    ],
  },
  {
    id: 'transactionDetails',
    title: 'Transaction Details',
    layout: 'double',
    fields: [
      { type: 'field', label: 'Transaction ID', path: 'transactionId' },
      { type: 'field', label: 'Transaction Date', path: 'transactionDate' },
      { type: 'field', label: 'Processing Date', path: 'processingDate' },
      { type: 'field', label: 'Settlement Date', path: 'settlementDate' },
      { type: 'field', label: 'Authorization Code', path: 'authorizationCode' },
    ],
  },
  {
    id: 'parties',
    title: 'Parties',
    layout: 'single',
    fields: [
      {
        type: 'object',
        label: 'Payer',
        path: 'payer',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Account Number', path: 'accountNumber' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Tax ID', path: 'taxId' },
          { label: 'Customer Number', path: 'customerNumber' },
        ],
      },
      {
        type: 'object',
        label: 'Payee',
        path: 'payee',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Account Number', path: 'accountNumber' },
          { label: 'Address', path: 'address' },
          { label: 'Contact', path: 'contact' },
          { label: 'Tax ID', path: 'taxId' },
          { label: 'Vendor Number', path: 'vendorNumber' },
        ],
      },
    ],
  },
  {
    id: 'financialInstitution',
    title: 'Financial Institution',
    layout: 'double',
    fields: [
      {
        type: 'object',
        label: 'Financial Institution',
        path: 'financialInstitution',
        fields: [
          { label: 'Name', path: 'name' },
          { label: 'Branch Name', path: 'branchName' },
          { label: 'Branch Code', path: 'branchCode' },
          { label: 'SWIFT Code', path: 'swiftCode' },
          { label: 'Routing Number', path: 'routingNumber' },
        ],
      },
    ],
  },
  {
    id: 'paymentBreakdown',
    title: 'Payment Breakdown',
    layout: 'single',
    fields: [
      {
        type: 'array',
        label: 'Payment Breakdown',
        path: 'paymentBreakdown',
        columns: [
          { label: 'Invoice Number', path: 'invoiceNumber' },
          { label: 'Description', path: 'description' },
          { label: 'Original Amount', path: 'originalAmount' },
          { label: 'Paid Amount', path: 'paidAmount' },
          { label: 'Discount Amount', path: 'discountAmount' },
          { label: 'Adjustment Amount', path: 'adjustmentAmount' },
          { label: 'Remaining Balance', path: 'remainingBalance' },
          { label: 'Payment Date', path: 'paymentDate' },
          { label: 'Due Date', path: 'dueDate' },
        ],
      },
    ],
  },
  {
    id: 'fees',
    title: 'Fees',
    layout: 'single',
    fields: [
      {
        type: 'array',
        label: 'Fees',
        path: 'fees',
        columns: [
          { label: 'Description', path: 'description' },
          { label: 'Amount', path: 'amount' },
        ],
      },
      { type: 'field', label: 'Total Fees', path: 'totalFees' },
    ],
  },
  {
    id: 'exchangeRate',
    title: 'Exchange Rate Information',
    layout: 'double',
    fields: [
      {
        type: 'object',
        label: 'Exchange Rate',
        path: 'exchangeRate',
        fields: [
          { label: 'From Currency', path: 'fromCurrency' },
          { label: 'To Currency', path: 'toCurrency' },
          { label: 'Rate', path: 'rate' },
          { label: 'Date', path: 'date' },
        ],
      },
    ],
  },
  {
    id: 'additionalInfo',
    title: 'Additional Information',
    layout: 'single',
    fields: [
      { type: 'field', label: 'Notes', path: 'notes', fullWidth: true },
      { type: 'field', label: 'Payment Instructions', path: 'paymentInstructions', fullWidth: true },
      { type: 'field', label: 'Receipt Available', path: 'receiptAvailable' },
      { type: 'field', label: 'Receipt URL', path: 'receiptUrl' },
    ],
  },
  {
    id: 'signature',
    title: 'Electronic Signature',
    layout: 'double',
    fields: [
      {
        type: 'object',
        label: 'Signature',
        path: 'signature',
        fields: [
          { label: 'Signed By', path: 'signedBy' },
          { label: 'Signature Date', path: 'signatureDate' },
          { label: 'Signature Image', path: 'signatureImage' },
          { label: 'Verification Code', path: 'verificationCode' },
        ],
      },
    ],
  },
]

const PaymentConfirmationDialog: React.FC<PaymentConfirmationDialogProps> = ({
  data,
  onSave,
  shipmentId,
  documentId,
}) => {
  return (
    <DocumentDialog
      documentData={data}
      schema={{}} // TODO: Import paymentConfirmationSchema from backend
      sections={paymentConfirmationSections}
      documentType="Payment Confirmation"
      onSave={onSave}
      shipmentId={shipmentId}
      documentId={documentId}
    />
  )
}

export default PaymentConfirmationDialog